import React, {useState, useEffect, useCallback} from "react";
import ConfirmationDialog from "../shared/ConfirmationDialog";
import "./FeaturesPage.css";
import {toast} from "react-toastify";
import {
  useCreateProductFeatureMutation,
  useGetAllProdcutsFeaturesQuery,
  useGetProductsQuery,
  useUpdateProductFeatureMutation,
} from "../../app/services/product";
import {
  useDeleteFeatureMutation,
} from "../../app/services/features";
// MUI
import {
  Box,
  Stack,
  Typography,
  Button,
  Chip,
  Tooltip,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Pagination,
  Skeleton,
  InputAdornment,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import ClearIcon from "@mui/icons-material/Clear";

// Simple highlighter for matched substrings
const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const highlightTerm = (text: string, term: string, onDark?: boolean) => {
  const value = text || "";
  const q = (term || "").trim();
  if (!q) return <>{value}</>;
  try {
    const re = new RegExp(`(${escapeRegExp(q)})`, "ig");
    const parts = value.split(re);
    return (
      <>
        {parts.map((p, i) =>
          re.test(p) ? (
            <Box
              key={`${p}-${i}`}
              component="mark"
              sx={{
                px: 0.25,
                borderRadius: 0.5,
                color: onDark ? 'inherit' : 'inherit',
                bgcolor: onDark ? 'rgba(255,255,255,0.25)' : 'rgba(255,235,59,0.45)',
              }}
            >
              {p}
            </Box>
          ) : (
            <React.Fragment key={`${p}-${i}`}>{p}</React.Fragment>
          )
        )}
      </>
    );
  } catch {
    return <>{value}</>;
  }
};


const FeaturesPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10); // Items per page
  const [totalPages, setTotalPages] = useState(1);
  const [totalRows, setTotalRows] = useState<number | null>(null);
  const [productswithfeatures, setProductswithFeatures] = useState<
    ProductWithFeaturesDto[]
  >([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(
    new Set(),
  );
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState<Feature | null>(null);
  const [selectedProductId, setSelectedProductId] = useState<string>("");
  const [newFeature, setNewFeature] = useState({
    name: "",
    description: "",
    productId: "" as string,
    parentId: null as string | null,
  });
  const [editFeature, setEditFeature] = useState({
    featureId: "",
    name: "",
    description: "",
    productId: "" as string,
  });

  // Inline editing state
  const [inlineEditingId, setInlineEditingId] = useState<string | null>(null);
  const [inlineEditValue, setInlineEditValue] = useState("");

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [featureToDelete, setFeatureToDelete] = useState<string | null>(null);
  // Global client-side search (reuse existing paginated API; no extra calls)
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search), 250);
    return () => window.clearTimeout(t);
  }, [search]);

  // While searching, request a larger page size once to minimize pagination misses
  const effectivePage = debouncedSearch ? 1 : page;
  // Cap to avoid over-fetching; adjust as needed (dataset is small in admin UI)
  const allRows = totalRows && totalRows > 0 ? Math.min(totalRows, 5000) : 5000;
  const effectivePageSize = debouncedSearch ? allRows : pageSize;
  const filteredProducts = React.useMemo(() => {
    const term = (debouncedSearch || "").trim().toLowerCase();
    if (!term) return productswithfeatures;
    return (productswithfeatures || []).filter((p: ProductWithFeaturesDto) => {
      const inProduct = (p.productName || "").toLowerCase().includes(term);
      const inFeatures = (p.features || []).some((ft: ProductFeatureDto) => (ft.featureName || "").toLowerCase().includes(term));
      return inProduct || inFeatures;
    });
  }, [productswithfeatures, debouncedSearch]);

  // Auto-expand matching products while searching for better visibility
  useEffect(() => {
    if (debouncedSearch) {
      const next = new Set<string>((filteredProducts || []).map(p => p.productName));
      setExpandedProducts(next);
    }
  }, [debouncedSearch, filteredProducts]);

  // Screen-reader live updates for results count
  const [liveMsg, setLiveMsg] = useState("");
  useEffect(() => {
    if (debouncedSearch) {
      setLiveMsg(`${filteredProducts.length} result${filteredProducts.length === 1 ? '' : 's'} for ${debouncedSearch}`);
    } else {
      setLiveMsg("");
    }
  }, [debouncedSearch, filteredProducts]);
  const {
    data: productsWithFeatures,
    isLoading,
    error: ProductsError,
    refetch: refetchProductsWithFeatures,
  } = useGetAllProdcutsFeaturesQuery({page: effectivePage, pageSize: effectivePageSize});
  const [createProductFeature, {isLoading: isCreating}] =
    useCreateProductFeatureMutation();
  const [updateProductFeature, {isLoading: isUpdating}] =
    useUpdateProductFeatureMutation();
  const [deleteProductFeature, {isLoading: isDeleting}] =
    useDeleteFeatureMutation();
  const {
    data: productsData,
    isFetching: productsIsFetching,
    isSuccess: productIsSuccess,
    isError: productsIsError,
    error: productsError,
    //refetch: refetchProducts,
  } = useGetProductsQuery(
    {},
    {
      refetchOnMountOrArgChange: true,
    },
  );

  useEffect(() => {
    if (productIsSuccess && productsData?.data) {
      const filteredProducts = productsData.data.filter(
        (p: {productName: string}) => p.productName !== "IDMSPortal",
      );
      setProducts(filteredProducts);
    }

    if (productsWithFeatures && productsWithFeatures.data) {
      setProductswithFeatures(productsWithFeatures.data);
      setTotalPages(productsWithFeatures.pageCount);
      if (typeof (productsWithFeatures as any).rowCount === 'number') {
        setTotalRows((productsWithFeatures as any).rowCount);
      }
    }
    setLoading(false);
    setError(null);
  }, [productsWithFeatures, productsData, productIsSuccess]);

  // Stable onChange handlers to prevent focus loss
  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setNewFeature(prev => ({...prev, description: e.target.value}));
    },
    [],
  );

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setNewFeature(prev => ({ ...prev, name: e.target.value }));
    },
    [],
  );

  const handleProductIdChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setNewFeature(prev => ({...prev, productId: e.target.value}));
    },
    [],
  );

  const handleCreateFeature = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!newFeature.name.trim()) {
      setError("Feature name is required.");
      return;
    }

    if (!newFeature.description.trim()) {
      setError("Feature description is required.");
      return;
    }

    if (!String(newFeature.productId)) {
      setError("Please select a product.");
      return;
    }

    try {
      // Prepare the request payload
      const createRequest = {
        productId: newFeature.productId,
        body: {
          productFeatures: [
            {
              productFeatureName: newFeature.name,
              description: newFeature.description,
            },
          ],
        },
      };

      console.log("Sending create request:", createRequest);

      // Call the mutation API
      const newFeatureResponse = await createProductFeature(
        createRequest,
      ).unwrap();

      // Reset form and close modal
      setNewFeature({name: "", description: "", productId: "", parentId: null});
      setShowCreateForm(false);
      setError(null);
      refetchProductsWithFeatures();
      toast.success("Feature created successfully!");
    } catch (err: any) {
      console.error("Error creating feature:", err);
      console.error("Error response:", err.response?.data);
      setError("Failed to create feature. Please try again.");
      toast.error("Failed to create feature. Please try again.");

      // Provide more specific error messages
      if (err.response?.status === 400) {
        const errorMessage =
          err.response?.data?.message ||
          err.response?.data ||
          "Invalid data provided";
        setError(`Failed to create feature: ${errorMessage}`);
      } else if (err.response?.status === 404) {
        setError("Product not found. Please select a valid product.");
      } else {
        setError("Failed to create feature. Please try again.");
      }
    }
  };

  // Inline name save handler (outside other handlers so it is in component scope)
  const handleInlineSave = useCallback(async (feature: ProductFeatureDto) => {
    try {
      const newName = inlineEditValue.trim();
      if (!newName) {
        toast.error('Feature name cannot be empty');
        return;
      }
      const updateRequest: UpdateProductFeatureReq = {
        productFeatureId: feature.featureId,
        productFeature: {
          productFeatureName: newName,
          description: feature.description,
          productId: feature.productId,
        },
      };
      await updateProductFeature(updateRequest).unwrap();
      setInlineEditingId(null);
      toast.success('Feature updated');
      refetchProductsWithFeatures();
    } catch (e: any) {
      toast.error(e?.data?.title || 'Update failed');
    }
  }, [inlineEditValue, updateProductFeature, refetchProductsWithFeatures]);

  const handleEditFeature = (feature: ProductFeatureDto) => {
    setSelectedFeature({
      id: feature.featureId,
      productFeatureName: feature.featureName,
      description: feature.description || "",
      createdDate: "", // You can populate this if needed
    });
    setEditFeature({
      featureId: feature.featureId,
      name: feature.featureName,
      description: feature.description,
      productId: feature.productId,
    });
    setShowEditForm(true);
  };

  const handleUpdateFeature = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editFeature) return;

    try {
      const updateRequest: UpdateProductFeatureReq = {
        productFeatureId: editFeature.featureId,
        productFeature: {
          productFeatureName: editFeature.name,
          description: editFeature.description,
          productId: editFeature.productId,
        },
      };

      var response = await updateProductFeature(updateRequest).unwrap();

      setShowEditForm(false);
      setEditFeature({
        featureId: "",
        name: "",
        description: "",
        productId: "" as string,
      });
      refetchProductsWithFeatures();
      toast.success("Feature updated successfully!");
    } catch (err) {
      console.error("Error updating feature:", err);
      setError("Failed to update feature. Please try again.");
      toast.error("Failed to update feature. Please try again.");
    }
  };

  const handleDeleteFeature = (id: string) => {
    setFeatureToDelete(id);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteFeature = async () => {
    if (!featureToDelete) return;

    try {
      var response = await deleteProductFeature(featureToDelete);
      toast.success("Feature deleted successfully!");
      setShowDeleteConfirm(false);
      setFeatureToDelete(null);
      refetchProductsWithFeatures();
    } catch (err) {
      console.error("Error deleting feature:", err);
      const anyErr: any = err;
      const status = anyErr?.response?.status;
      const serverMsg =
        anyErr?.response?.data?.message ||
        anyErr?.response?.data?.title ||
        anyErr?.response?.data?.detail ||
        (typeof anyErr?.response?.data === "string"
          ? anyErr.response.data
          : "");

      let friendly = "";
      if (status >= 500) {
        friendly =
          "Cannot delete this feature because it is referenced (e.g., assigned to a subscription tier or as an add-on). Remove related assignments first, then try again.";
      } else if (status === 409) {
        friendly =
          "Cannot delete this feature due to existing dependencies. Resolve them and try again.";
      } else if (status === 400) {
        friendly = `Bad request${serverMsg ? `: ${serverMsg}` : ""}`;
      }

      const message =
        friendly || serverMsg || "Failed to delete feature. Please try again.";
      toast.error(message);
      setError(message);
      setShowDeleteConfirm(false);
      setFeatureToDelete(null);
    }
  };

  const cancelDeleteFeature = () => {
    setShowDeleteConfirm(false);
    setFeatureToDelete(null);
  };

  const toggleProductExpansion = (productName: string) => {
    const newExpanded = new Set(expandedProducts);
    if (newExpanded.has(productName)) {
      newExpanded.delete(productName);
    } else {
      newExpanded.add(productName);
    }
    setExpandedProducts(newExpanded);
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Skeleton variant="text" width={260} height={36} />
          <Skeleton variant="rounded" height={40} sx={{ my: 2 }} />
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={56} sx={{ my: 1 }} />
          ))}
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: 2 }}>
      {/* Sticky search + header */}
      <Paper variant="outlined" sx={{ position: 'sticky', top: 0, zIndex: (theme) => theme.zIndex.appBar + 1, p: 2, mb: 2, bgcolor: 'background.paper', borderLeft: (theme) => `4px solid ${theme.palette.primary.main}` }}>
        <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="h5" fontWeight={700} color="text.primary">Products and Features</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage product features — hierarchy of features not supported at this time
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowCreateForm(true)}>
            Create Feature
          </Button>
        </Stack>
        {/* Global search across current page data */}
        <Box sx={{ mt: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by product or feature"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            inputProps={{ 'aria-label': 'Search by product or feature' }}
            InputProps={{
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" aria-label="Clear search" onClick={() => setSearch("")}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>
      </Paper>
      {/* SR-only live region for search results updates */}
      <Box component="div" sx={{ position: 'absolute', left: -10000, width: 1, height: 1, overflow: 'hidden' }} aria-live="polite">
        {liveMsg}
      </Box>
      {isLoading && (
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading features...</p>
        </div>
      )}
      {/* Create Feature Modal */}
      {showCreateForm && (
        <Dialog open onClose={() => setShowCreateForm(false)} fullWidth maxWidth="sm">
          <DialogTitle>Create New Feature</DialogTitle>
          <form onSubmit={handleCreateFeature}>
            <DialogContent dividers>
              <Stack spacing={2}>
                <TextField
                  id="featureName"
                  label="Feature Name"
                  value={newFeature.name}
                  onChange={handleNameChange}
                  placeholder="e.g., Authentication"
                  required
                  fullWidth
                />
                <TextField
                  id="featureDescription"
                  label="Feature Description"
                  value={newFeature.description}
                  onChange={handleDescriptionChange}
                  placeholder="e.g., User Authentication, Dashboard Analytics"
                  required
                  fullWidth
                />
                <Select
                  id="productSelect"
                  value={newFeature.productId}
                  onChange={(e) => handleProductIdChange(e as any)}
                  displayEmpty
                  fullWidth
                  required
                >
                  <MenuItem value=""><em>Select a product</em></MenuItem>
                  {products.map(product => (
                    <MenuItem key={product.id} value={String(product.id)}>
                      {product.productName}
                    </MenuItem>
                  ))}
                </Select>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowCreateForm(false)} color="inherit">Cancel</Button>
              <Button type="submit" variant="contained" disabled={isCreating}>{isCreating ? "Creating..." : "Create Feature"}</Button>
            </DialogActions>
          </form>
        </Dialog>
      )}
      {showEditForm && selectedFeature && (
        <Dialog open onClose={() => setShowEditForm(false)} fullWidth maxWidth="sm">
          <DialogTitle>Edit Feature</DialogTitle>
          <form onSubmit={handleUpdateFeature}>
            <DialogContent dividers>
              <Stack spacing={2}>
                <TextField
                  id="editFeatureName"
                  label="Feature Name"
                  value={editFeature.name}
                  onChange={(e) => setEditFeature(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Authentication"
                  required
                  fullWidth
                />
                <TextField
                  id="editFeatureDescription"
                  label="Feature Description"
                  value={editFeature.description}
                  onChange={(e) => setEditFeature(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="e.g., User Authentication, Dashboard Analytics"
                  fullWidth
                />
                <Select
                  id="editProductSelect"
                  value={editFeature.productId}
                  onChange={(e) => setEditFeature(prev => ({ ...prev, productId: (e.target.value as string) }))}
                  displayEmpty
                  fullWidth
                  required
                >
                  <MenuItem value=""><em>Select a product</em></MenuItem>
                  {products.map(product => (
                    <MenuItem key={product.id} value={String(product.id)}>
                      {product.productName}
                    </MenuItem>
                  ))}
                </Select>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowEditForm(false)} color="inherit">Cancel</Button>
              <Button type="submit" variant="contained" disabled={isUpdating}>{isUpdating ? "Updating..." : "Update Feature"}</Button>
            </DialogActions>
          </form>
        </Dialog>
      )}
      {ProductsError && (
        <div className="error-message">
          Failed to load products and features. Please try again.
        </div>
      )}
      {!isLoading && productswithfeatures.length > 0 && (
        <Stack spacing={2}>
          {filteredProducts && filteredProducts.length > 0 ? (
            filteredProducts.map((product: ProductWithFeaturesDto) => {
              const isExpanded = expandedProducts.has(product.productName);
              const productNameMatches = !!debouncedSearch && (product.productName || '').toLowerCase().includes(debouncedSearch.toLowerCase());
              return (
                <Accordion key={product.productId} expanded={isExpanded} onChange={() => toggleProductExpansion(product.productName)} sx={{ bgcolor: 'background.paper', borderRadius: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: '8px' }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                      <Typography fontWeight={600}>
                        {highlightTerm(product.productName, debouncedSearch, true)}
                      </Typography>
                      <Tooltip title={isExpanded ? '' : `${product.features.length} features`} disableHoverListener={isExpanded}>
                        <Chip size="small" color="default" label={`${product.features.length}`} sx={{ bgcolor: 'background.paper' }} />
                      </Tooltip>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Paper variant="outlined" sx={{ p: 1 }}>
                      <Stack divider={<Divider />}>
                        {(productNameMatches ? product.features : product.features
                          .filter((f: ProductFeatureDto) => !debouncedSearch || (f.featureName || '').toLowerCase().includes(debouncedSearch.toLowerCase())))
                          .map((feature: ProductFeatureDto) => {
                          const isInline = inlineEditingId === feature.featureId;
                          return (
                            <Stack key={feature.featureId} direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" sx={{ py: 1 }} spacing={1}>
                              <Box sx={{ flex: 1, minWidth: 200 }} onDoubleClick={() => { setInlineEditingId(feature.featureId); setInlineEditValue(feature.featureName); }}>
                                {isInline ? (
                                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                                    <TextField
                                      size="small"
                                      fullWidth
                                      value={inlineEditValue}
                                      onChange={(e) => setInlineEditValue(e.target.value)}
                                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleInlineSave(feature); } if (e.key === 'Escape') { e.preventDefault(); setInlineEditingId(null); setInlineEditValue(''); } }}
                                      inputProps={{ 'aria-label': 'Feature name' }}
                                    />
                                    <Tooltip title="Save">
                                      <span>
                                        <IconButton size="small" color="primary" onClick={() => handleInlineSave(feature)}>
                                          <SaveIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    <Tooltip title="Cancel">
                                      <IconButton size="small" color="inherit" onClick={() => { setInlineEditingId(null); setInlineEditValue(''); }}>
                                        <CloseIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                ) : (
                                  <Tooltip title="Double-click to quick edit name">
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ cursor: 'text', minWidth: 0 }}>
                                      <Typography variant="body1" color="text.primary" noWrap>
                                        {highlightTerm(feature.featureName, debouncedSearch)}
                                      </Typography>
                                      {!!feature.description && (
                                        <Tooltip title={feature.description} placement="top" arrow>
                                          <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: { xs: 200, sm: 360 }, textOverflow: 'ellipsis', overflow: 'hidden' }}>
                                            {feature.description}
                                          </Typography>
                                        </Tooltip>
                                      )}
                                    </Stack>
                                  </Tooltip>
                                )}
                              </Box>
                              <Stack direction="row" spacing={1}>
                                <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => handleEditFeature(feature)}>
                                  Edit
                                </Button>
                                <Button size="small" color="error" variant="outlined" startIcon={<DeleteIcon />} onClick={() => handleDeleteFeature(feature.featureId)}>
                                  Delete
                                </Button>
                              </Stack>
                            </Stack>
                          );
                        })}
                      </Stack>
                    </Paper>
                  </AccordionDetails>
                </Accordion>
              );
            })
          ) : debouncedSearch ? (
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h6">No results found</Typography>
              <Typography variant="body2" color="text.secondary">Try a different search term</Typography>
            </Paper>
          ) : (
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h6">No Features Found</Typography>
              <Typography variant="body2" color="text.secondary">Create features to organize your product functionality</Typography>
            </Paper>
          )}

          {/* Hide pagination while searching; show total results instead */}
          {debouncedSearch ? (
            <Stack alignItems="center" sx={{ py: 1 }}>
              <Chip label={`${filteredProducts.length} result${filteredProducts.length === 1 ? '' : 's'}`} />
            </Stack>
          ) : (
            <Stack alignItems="center" sx={{ py: 1 }}>
              <Pagination
                color="primary"
                count={totalPages}
                page={page}
                onChange={(_e, value) => setPage(value)}
                showFirstButton
                showLastButton
              />
            </Stack>
          )}
        </Stack>
      )}
      {(
        <ConfirmationDialog
          isOpen={showDeleteConfirm}
          title="Delete Feature"
          message={`Are you sure you want to delete this feature? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={confirmDeleteFeature}
          onCancel={cancelDeleteFeature}
          isDangerous={true}
        />
      )}
    </Box>
  );
};

export default FeaturesPage;
